import { useCallback, useEffect, useMemo } from 'react';
import { create } from 'zustand';

import {
  makeAssetMemory,
  makeBenchmarkColor,
  makeComparisonBenchmarkQuotes,
} from '@mais-retorno-front/data-access-test-2/factories';
import { IBenchmarkQuotes } from '@mais-retorno-front/data-access-test-2/interfaces';

interface IUseStore {
  selectedBenchmarks: string[];
  selectedBenchmarkTab: 'cdi' | 'ibov';
  loading: boolean;
  benchmarkQuotes: any[];
  setLoading: (isLoading: boolean) => void;
  setBenchmarkQuotes: (quotes: any[]) => void;
  setSelectedBenchmarks: (items: string[]) => void;
  setSelectedBenchmarkTab: (benchmark: any) => void;
}

const benchmarkTabsIsValid = (benchmark: string): 'cdi' | 'ibov' => {
  return benchmark === 'cdi' || benchmark === 'ibov' ? benchmark : 'cdi';
};

const useStore = create<IUseStore>((set) => ({
  relativeBenchmarkStats: null,
  selectedBenchmarks: [],
  selectedBenchmarkTab: 'cdi',
  benchmarkQuotes: [],
  loading: true,
  setLoading: (isLoading: boolean) => set({ loading: isLoading }),
  setBenchmarkQuotes: (benchmarkQuotes: any[]) => set({ benchmarkQuotes }),
  setSelectedBenchmarks: (selectedBenchmarks: string[]) =>
    set({ selectedBenchmarks }),
  setSelectedBenchmarkTab: (benchmark: string) =>
    set({ selectedBenchmarkTab: benchmarkTabsIsValid(benchmark) }),
}));

export const useSheetFundsBenchmarks = ({ initialBenchmark, fundSlug }) => {
  const selectedBenchmarks = useStore((store) => store.selectedBenchmarks);
  const benchmarkQuotes = useStore((store) => store.benchmarkQuotes);
  const selectedBenchmarkTab = useStore((store) => store.selectedBenchmarkTab);

  const setBenchmarkQuotes = useStore((store) => store.setBenchmarkQuotes);
  const setSelectedBenchmarks = useStore(
    (store) => store.setSelectedBenchmarks
  );
  const setSelectedBenchmarkTab = useStore(
    (store) => store.setSelectedBenchmarkTab
  );

  const benchmarkColorRepo = useMemo(() => makeBenchmarkColor(), []);

  const benchmarkQuotesMemoryRepo = useMemo(
    () => makeAssetMemory<IBenchmarkQuotes>(),
    []
  );

  useEffect(() => {
    setSelectedBenchmarks([initialBenchmark]);
    setSelectedBenchmarkTab(initialBenchmark);

    getBenchmarkQuotes([initialBenchmark]);
  }, [initialBenchmark]);

  const getBenchmarkQuotes = useCallback(
    async (benchmarks: string[]) => {
      const { benchmarkQuotes } = await makeComparisonBenchmarkQuotes(
        benchmarkColorRepo,
        benchmarkQuotesMemoryRepo
      ).execute(benchmarks);

      setBenchmarkQuotes(benchmarkQuotes);
    },
    [benchmarkColorRepo, benchmarkQuotesMemoryRepo, setBenchmarkQuotes]
  );

  const onChangeSelectedBenchmarks = (benchmarks: string[]) => {
    setSelectedBenchmarks(benchmarks);
    getBenchmarkQuotes(benchmarks);
  };

  return {
    selectedBenchmarks,
    benchmarkQuotes,
    selectedBenchmarkTab,
    setSelectedBenchmarkTab,
    onChangeSelectedBenchmarks,
  };
};
